import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, CheckCircle2, FileText, DollarSign, Zap } from "lucide-react";
import { api, errMsg, fmtDate, fmtDateOnly, fmtMoney } from "../../lib/api";
import { DataTable, Td, StatusBadge, EmptyState, StatCard, FormDialog, ActionButton, Field, TextInput } from "../common";

const StageChip = ({ stage }) =>
  stage ? (
    <span className="px-2 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-[10px] font-mono">
      Stage {stage}
    </span>
  ) : (
    <span className="text-slate-600 text-xs">—</span>
  );

const endOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toLocaleDateString("en-CA");
};

const BatchBillDialog = ({ open, onOpenChange, onDone }) => {
  const [cutoff, setCutoff] = useState(endOfMonth());
  const [separate, setSeparate] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setPreview(null); setCutoff(endOfMonth()); setSeparate(true); }
  }, [open]);

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/proformas/batch-preview", { cutoff_date: cutoff, separate_stages: separate });
      setPreview(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const process = async () => {
    setBusy(true);
    try {
      const r = await api.post("/proformas/batch-process", { cutoff_date: cutoff, separate_stages: separate });
      toast.success(`Created ${r.data.count} draft Proforma${r.data.count === 1 ? "" : "s"} · ${fmtMoney(r.data.total)}`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sum = preview?.summary;
  const billable = (preview?.rentals || []).filter((p) => !p.skipped);
  const skipped = (preview?.rentals || []).filter((p) => p.skipped);

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title="Process Month-End Billing" testId="batch-bill-dialog" className="max-w-2xl">
      <div className="space-y-5">
        <p className="text-sm text-slate-400">
          Drafts a Proforma for every active rental's unbilled days through the cut-off date. Already-billed periods are skipped automatically — nothing is charged twice.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Bill through (cut-off date)">
            <TextInput testId="batch-cutoff-input" type="date" value={cutoff} onChange={(e) => { setCutoff(e.target.value); setPreview(null); }} />
          </Field>
          <Field label="Billing split">
            <button
              type="button"
              data-testid="batch-separate-toggle"
              onClick={() => { setSeparate((v) => !v); setPreview(null); }}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm transition-colors ${separate ? "bg-indigo-500/10 border-indigo-500/40 text-indigo-200" : "bg-slate-800/40 border-slate-700 text-slate-300"}`}
            >
              <span>{separate ? "Separate Proforma per stage" : "One combined Proforma per rental"}</span>
              <span className={`ml-3 inline-flex h-5 w-9 items-center rounded-full transition-colors ${separate ? "bg-indigo-500" : "bg-slate-600"}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${separate ? "translate-x-4" : "translate-x-1"}`} />
              </span>
            </button>
          </Field>
        </div>

        {!preview ? (
          <ActionButton testId="batch-preview-button" onClick={runPreview} disabled={busy} className="w-full justify-center py-2.5">
            {busy ? "Calculating…" : "Preview what will be billed"}
          </ActionButton>
        ) : (
          <div className="space-y-4" data-testid="batch-preview-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Rentals</p>
                <p className="text-lg font-semibold text-slate-100" data-testid="batch-sum-rentals">{sum.rentals_to_bill}</p>
              </div>
              <div className="rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Proformas</p>
                <p className="text-lg font-semibold text-indigo-300" data-testid="batch-sum-proformas">{sum.proforma_count}</p>
              </div>
              <div className="rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Grand Total</p>
                <p className="text-lg font-semibold text-emerald-400" data-testid="batch-sum-total">{fmtMoney(sum.grand_total)}</p>
              </div>
            </div>

            {billable.length === 0 ? (
              <EmptyState message="Nothing to bill for this cut-off date — all active rentals are already billed through it." testId="batch-nothing-empty" />
            ) : (
              <div className="max-h-64 overflow-y-auto border border-slate-800 rounded-md divide-y divide-slate-800" data-testid="batch-plan-list">
                {billable.map((r) => (
                  <div key={r.dispatch_id} className="px-3 py-2" data-testid={`batch-plan-${r.dispatch_number}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-amber-400 text-sm">{r.dispatch_number}</span>
                      <span className="text-xs text-slate-400">{r.customer_name}</span>
                    </div>
                    {r.proformas.map((pf, i) => (
                      <div key={`${r.dispatch_id}-${pf.from}-${pf.to}-${pf.stage || (pf.stages || []).join("") || i}`} className="flex items-center justify-between text-xs text-slate-400 mt-1 pl-1">
                        <span className="flex items-center gap-2">
                          {pf.stage ? <StageChip stage={pf.stage} /> : <span className="text-indigo-300 font-mono text-[10px]">Stages {(pf.stages || []).join(", ")}</span>}
                          <span>{fmtDateOnly(pf.from)} → {fmtDateOnly(pf.to)}</span>
                        </span>
                        <span className="font-mono text-emerald-400">{fmtMoney(pf.total)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {skipped.length > 0 && (
              <p className="text-xs text-slate-500" data-testid="batch-skipped-note">
                {skipped.length} rental{skipped.length === 1 ? "" : "s"} skipped (already billed through this date).
              </p>
            )}

            <div className="flex gap-3">
              <ActionButton testId="batch-back-button" variant="secondary" onClick={() => setPreview(null)} disabled={busy} className="justify-center">
                Change
              </ActionButton>
              <ActionButton testId="batch-process-button" onClick={process} disabled={busy || sum.proforma_count === 0} className="flex-1 justify-center py-2.5">
                {busy ? "Processing…" : `Process ${sum.proforma_count} Draft${sum.proforma_count === 1 ? "" : "s"}`}
              </ActionButton>
            </div>
          </div>
        )}
      </div>
    </FormDialog>
  );
};

export const PeriodicBilling = () => {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [billingId, setBillingId] = useState(null);

  const load = useCallback(() => {
    api.get("/proformas/billing-overview").then((r) => setData(r.data)).catch(() => setData({ summary: {}, rentals: [], proformas: [] }));
  }, []);

  useEffect(() => { load(); }, [load]);

  const billRow = async (r) => {
    setBillingId(r.dispatch_id);
    try {
      const resp = await api.post("/proformas/batch-process", { cutoff_date: endOfMonth(), separate_stages: true, dispatch_ids: [r.dispatch_id] });
      if (resp.data.count > 0) {
        toast.success(`${r.dispatch_number}: ${resp.data.count} draft Proforma${resp.data.count === 1 ? "" : "s"} · ${fmtMoney(resp.data.total)}`);
      } else {
        toast.info(`${r.dispatch_number}: nothing to bill through ${fmtDateOnly(endOfMonth())}`);
      }
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBillingId(null); }
  };

  if (data === null) return <EmptyState message="Loading billing overview…" testId="periodic-billing-loading" />;

  const s = data.summary || {};

  return (
    <div data-testid="periodic-billing-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-indigo-500/5 border border-indigo-500/30 rounded-lg px-5 py-4 mb-8" data-testid="periodic-billing-note">
        <p className="text-sm text-indigo-200/90 leading-relaxed">
          <span className="font-mono uppercase tracking-widest text-xs text-indigo-400 block mb-1">Manual Periodic Billing</span>
          Billing periods are tracked directly on each rental ticket. Run month-end billing to draft Proformas for every unbilled rental at once.
        </p>
        <ActionButton testId="batch-bill-open-button" onClick={() => setDialog(true)} className="shrink-0">
          <Zap className="h-4 w-4 mr-2" /> Process Month-End Billing
        </ActionButton>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
        <StatCard testId="pb-stat-active" label="Active Rentals" value={s.active_rentals ?? 0} icon={CalendarClock} accent="text-sky-400" />
        <StatCard testId="pb-stat-needs-billing" label="Needs Billing" value={s.needs_billing ?? 0} icon={AlertTriangle} accent="text-amber-400" />
        <StatCard testId="pb-stat-up-to-date" label="Up To Date" value={s.up_to_date ?? 0} icon={CheckCircle2} accent="text-emerald-400" />
        <StatCard testId="pb-stat-proforma-total" label="Proforma Value" value={fmtMoney(s.proforma_total)} icon={DollarSign} accent="text-indigo-400" />
      </div>

      <div className="mb-10">
        <div className="mb-4">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Active Rentals — Billing Status</p>
          <p className="text-sm text-slate-400">On-rent tickets with their manual billing coverage. Create a Proforma from the Rental Dispatch board to bill a period.</p>
        </div>
        {(!data.rentals || data.rentals.length === 0) ? (
          <EmptyState message="No active rentals right now." testId="pb-rentals-empty" />
        ) : (
          <DataTable
            testId="pb-rentals-table"
            columns={["Ticket", "Customer", "Location", "Stage", "Days On Rent", "Billed Through", "Periods", "Status", ""]}
            rows={data.rentals}
            renderRow={(r) => (
              <tr key={r.dispatch_id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`pb-rental-row-${r.dispatch_number}`}>
                <Td className="font-mono text-amber-400">{r.dispatch_number}</Td>
                <Td className="font-medium text-slate-100">{r.customer_name}</Td>
                <Td className="text-slate-400 text-xs max-w-[220px] truncate" >{r.delivery_location || "—"}</Td>
                <Td><StageChip stage={r.current_stage} /></Td>
                <Td className="font-mono text-slate-300">{r.days_on_rent ?? "—"}</Td>
                <Td className="text-slate-300 text-xs">{r.billed_through ? fmtDateOnly(r.billed_through) : "—"}</Td>
                <Td className="font-mono text-slate-400">{r.periods_count}{r.last_proforma && <span className="ml-1 text-[10px] text-slate-500">({r.last_proforma})</span>}</Td>
                <Td>
                  {r.unbilled ? (
                    <span data-testid={`pb-status-unbilled-${r.dispatch_number}`} className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] font-mono uppercase">Needs Billing</span>
                  ) : (
                    <span data-testid={`pb-status-current-${r.dispatch_number}`} className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase">Up To Date</span>
                  )}
                </Td>
                <Td>
                  {r.unbilled && (
                    <button
                      type="button"
                      data-testid={`pb-bill-now-${r.dispatch_number}`}
                      onClick={() => billRow(r)}
                      disabled={billingId === r.dispatch_id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 text-slate-950 text-xs font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50"
                    >
                      <Zap className="h-3.5 w-3.5" /> {billingId === r.dispatch_id ? "Billing…" : "Bill now"}
                    </button>
                  )}
                </Td>
              </tr>
            )}
          />
        )}
      </div>

      <div>
        <div className="mb-4">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Recent Proformas</p>
          <p className="text-sm text-slate-400">Latest billing documents issued across all rentals.</p>
        </div>
        {(!data.proformas || data.proformas.length === 0) ? (
          <EmptyState message="No Proforma Sales Orders yet." testId="pb-proformas-empty" />
        ) : (
          <DataTable
            testId="pb-proformas-table"
            columns={["Proforma", "Ticket", "Customer", "Stage", "Period", "Total", "Status", "Created"]}
            rows={data.proformas}
            renderRow={(p) => (
              <tr key={p.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`pb-proforma-row-${p.number}`}>
                <Td className="font-mono text-amber-400">{p.number}</Td>
                <Td className="font-mono text-slate-300">{p.dispatch_number || "—"}</Td>
                <Td className="font-medium text-slate-100">{p.customer_name || "—"}</Td>
                <Td><StageChip stage={p.stage} /></Td>
                <Td className="text-slate-400 text-xs">{p.billing_period ? `${fmtDateOnly(p.billing_period.from)} → ${fmtDateOnly(p.billing_period.to)}` : "—"}</Td>
                <Td className="font-mono text-emerald-400">{fmtMoney(p.total)}</Td>
                <Td><StatusBadge status={p.status} /></Td>
                <Td className="text-slate-500 text-xs">{fmtDate(p.created_at)}</Td>
              </tr>
            )}
          />
        )}
      </div>

      <BatchBillDialog open={dialog} onOpenChange={setDialog} onDone={load} />
    </div>
  );
};
